import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { CollectiblesService } from '../../../services/collectiblesService';
import marketplaceService from '../../../services/marketplaceService';

const CreateListingModal = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [collectibles, setCollectibles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    collectible_id: '',
    title: '',
    description: '',
    asking_price: '',
    currency: 'USD',
    condition: '',
    shipping_cost: '0',
    is_negotiable: true,
    shipping_details: {}
  });

  useEffect(() => {
    if (isOpen) {
      loadUserCollectibles();
    }
  }, [isOpen]);

  const loadUserCollectibles = async () => {
    const { data } = await CollectiblesService?.getUserCollectibles(user?.id);
    setCollectibles(data || []);
  };

  const handleCollectibleSelect = (collectibleId) => {
    const selected = collectibles?.find(c => c?.id === collectibleId);
    if (selected) {
      setFormData(prev => ({
        ...prev,
        collectible_id: collectibleId,
        title: selected?.name || '',
        description: selected?.description || '',
        asking_price: selected?.current_value || '',
        condition: selected?.condition || '',
        featured_image_url: selected?.primary_image_url || ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);

    const listingData = {
      ...formData,
      seller_id: user?.id,
      asking_price: parseFloat(formData?.asking_price),
      shipping_cost: parseFloat(formData?.shipping_cost || 0)
    };

    const { data, error } = await marketplaceService?.createListing(listingData);
    
    if (!error) {
      onSuccess?.();
    } else {
      alert('Failed to create listing: ' + error);
    }
    
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Plus" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-card-foreground">Create Marketplace Listing</h2>
              <p className="text-sm text-muted-foreground">List your collectible for sale</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Select Collectible */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Select Collectible <span className="text-destructive">*</span>
            </label>
            <Select
              value={formData?.collectible_id}
              onChange={(e) => handleCollectibleSelect(e?.target?.value)}
              required
            >
              <option value="">Choose a collectible...</option>
              {collectibles?.map(item => (
                <option key={item?.id} value={item?.id}>
                  {item?.name} - {item?.collectible_type}
                </option>
              ))}
            </Select>
          </div>

          {/* Title */}
          <Input
            label="Listing Title"
            required
            value={formData?.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e?.target?.value }))}
            placeholder="Enter listing title"
          />

          {/* Description */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Description <span className="text-destructive">*</span>
            </label>
            <textarea
              required
              value={formData?.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e?.target?.value }))}
              placeholder="Describe your item, its condition, and any important details"
              rows={4}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>

          {/* Condition */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Condition <span className="text-destructive">*</span>
            </label>
            <Select
              value={formData?.condition}
              onChange={(e) => setFormData(prev => ({ ...prev, condition: e?.target?.value }))}
              required
            >
              <option value="">Select condition...</option>
              <option value="mint">Mint</option>
              <option value="near-mint">Near Mint</option>
              <option value="excellent">Excellent</option>
              <option value="very-good">Very Good</option>
              <option value="good">Good</option>
              <option value="fair">Fair</option>
            </Select>
          </div>

          {/* Price & Shipping */}
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Asking Price"
              type="number"
              step="0.01"
              required
              value={formData?.asking_price}
              onChange={(e) => setFormData(prev => ({ ...prev, asking_price: e?.target?.value }))}
              placeholder="0.00"
            />
            <Input
              label="Shipping Cost"
              type="number"
              step="0.01"
              value={formData?.shipping_cost}
              onChange={(e) => setFormData(prev => ({ ...prev, shipping_cost: e?.target?.value }))}
              placeholder="0.00"
            />
          </div>

          {/* Negotiable */}
          <div className="flex items-center space-x-3 p-4 bg-muted/20 rounded-lg">
            <Checkbox
              checked={formData?.is_negotiable}
              onChange={(e) => setFormData(prev => ({ ...prev, is_negotiable: e?.target?.checked }))}
              id="negotiable"
            />
            <label htmlFor="negotiable" className="text-sm font-medium text-foreground cursor-pointer">
              Price is negotiable (buyers can make offers)
            </label>
          </div>

          {/* Actions */}
          <div className="flex space-x-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              disabled={loading}
              iconName="Plus"
              iconPosition="left"
              fullWidth
            >
              Create Listing
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateListingModal;
